/**
 * Expenses — the operational workspace.
 *
 * SINGLE SOURCE OF TRUTH: this reads and writes `acc_expenses` through the same
 * useExpenseApi hooks and the same ExpenseFormModal as the Expense Register.
 * There is no second dataset, no second form and no second service. The two
 * screens differ only in which slice they present and which actions they offer:
 *
 *   Expenses (this)        workflow oriented  — drafts and in-flight work
 *   Expense Register       financial register — every status, full filters
 *
 * Because both use the same React Query key, creating here refreshes the
 * register, the dashboard, reports and the approval queue automatically.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PlusCircle, Copy, Send, Pencil, Trash2, Ban } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useExpensesV2,
  useExpenseAction,
  useDeleteExpenseV2,
  type Expense,
  type ExpenseStatus,
} from '../hooks/useExpenseApi';
import { formatMinor, formatShortDate } from '../utils/money';
import { ExpenseFormModal } from './ExpenseFormModal';

const STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'info',
  rejected: 'danger',
  posted: 'primary',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'neutral',
  refunded: 'neutral',
};

const CELL = 'px-3 py-2 align-middle';
const HEAD = 'px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500';

/**
 * The workspace shows work that still needs a human: drafts, rejections and
 * items awaiting approval. Settled history lives in the register.
 */
const WORKSPACE_BUCKETS = [
  { id: 'open', label: 'Needs action', statuses: ['draft', 'rejected'] },
  { id: 'awaiting', label: 'Awaiting approval', statuses: ['submitted'] },
  { id: 'ready', label: 'Ready to post', statuses: ['approved'] },
  { id: 'all', label: 'Everything', statuses: [] },
] as const;

type BucketId = (typeof WORKSPACE_BUCKETS)[number]['id'];

export function ExpenseWorkspaceTab() {
  const [bucket, setBucket] = useState<BucketId>('open');
  const [formOpen, setFormOpen] = useState(false);
  const [target, setTarget] = useState<Expense | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'duplicate'>('create');

  // One query, one key. The register uses the same hook family, so a write here
  // invalidates both views at once.
  const { data, isLoading, isError, error } = useExpensesV2({ pageSize: 100 });
  const action = useExpenseAction();
  const remove = useDeleteExpenseV2();

  const all = data?.items ?? [];

  const rows = useMemo(() => {
    const spec = WORKSPACE_BUCKETS.find((b) => b.id === bucket);
    if (!spec || spec.statuses.length === 0) return all;
    return all.filter((e) => (spec.statuses as readonly string[]).includes(e.status));
  }, [all, bucket]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of WORKSPACE_BUCKETS) {
      map[b.id] = b.statuses.length === 0
        ? all.length
        : all.filter((e) => (b.statuses as readonly string[]).includes(e.status)).length;
    }
    return map;
  }, [all]);

  const openCreate = () => { setTarget(null); setFormMode('create'); setFormOpen(true); };
  const openEdit = (e: Expense) => { setTarget(e); setFormMode('edit'); setFormOpen(true); };
  const openDuplicate = (e: Expense) => { setTarget(e); setFormMode('duplicate'); setFormOpen(true); };

  const run = (e: Expense, verb: 'submit' | 'cancel', body?: Record<string, unknown>) => {
    action.mutate({ id: e._id, action: verb, body }, {
      onSuccess: () => toast.success(`${e.expenseNo} ${verb === 'submit' ? 'submitted for approval' : 'cancelled'}`),
      onError: (err: unknown) => toast.error(getApiErrorMessage(err) || `Could not ${verb} the expense`),
    });
  };

  return (
    <div className="space-y-3">
      {/* Bucket switcher + single create entry point */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {WORKSPACE_BUCKETS.map((b) => (
            <button
              key={b.id}
              onClick={() => setBucket(b.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                bucket === b.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              {b.label}
              <span className="ml-1.5 opacity-70">{counts[b.id] ?? 0}</span>
            </button>
          ))}
        </div>
        <Button onClick={openCreate}>
          <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" /> New expense
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        This workspace and the Expense Register read the same records. Anything created here appears
        in the register, reports and the approval queue immediately.
      </p>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : isError ? (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-rose-600">Could not load expenses.</p>
            <p className="mt-1 text-xs text-slate-500">{getApiErrorMessage(error) || 'Please retry in a moment.'}</p>
          </CardBody>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardBody>
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-slate-600">
                {bucket === 'all' ? 'No expenses captured yet' : 'Nothing in this bucket'}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {bucket === 'all'
                  ? 'Create one to start the draft → submit → approve → post → pay flow.'
                  : 'Switch to Everything to see all records.'}
              </p>
              {bucket === 'all' ? (
                <Button className="mt-3" onClick={openCreate}>
                  <PlusCircle className="mr-1 h-4 w-4" aria-hidden="true" /> New expense
                </Button>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200">
              <tr>
                <th className={HEAD}>Expense</th>
                <th className={HEAD}>Date</th>
                <th className={HEAD}>Category</th>
                <th className={HEAD}>Funded by</th>
                <th className={HEAD}>Total</th>
                <th className={HEAD}>Status</th>
                <th className={HEAD} />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const isDraft = ['draft', 'rejected'].includes(e.status);
                return (
                  <tr key={e._id} className="border-b border-slate-100">
                    <td className={CELL}>
                      <Link to={`/accounting-v2/expenses/${e._id}`} className="font-mono text-xs text-indigo-600 hover:underline">
                        {e.expenseNo}
                      </Link>
                      {e.description ? <p className="max-w-[240px] truncate text-xs text-slate-500">{e.description}</p> : null}
                      {e.status === 'rejected' && e.rejectionReason ? (
                        <p className="max-w-[240px] truncate text-xs text-rose-600" title={e.rejectionReason}>
                          {e.rejectionReason}
                        </p>
                      ) : null}
                    </td>
                    <td className={`${CELL} whitespace-nowrap text-xs`}>{formatShortDate(e.expenseDate)}</td>
                    <td className={`${CELL} text-xs`}>{e.categoryLabel || '—'}</td>
                    <td className={`${CELL} text-xs`}>{e.settlementMode.replace(/_/g, ' ')}</td>
                    <td className={`${CELL} font-medium`}>{formatMinor(e.grossAmountMinor, e.currency)}</td>
                    <td className={CELL}>
                      <Badge tone={STATUS_TONE[e.status] ?? 'neutral'}>{e.status.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className={CELL}>
                      <div className="flex justify-end gap-1.5">
                        {isDraft ? (
                          <Button size="sm" variant="secondary" onClick={() => run(e, 'submit')} disabled={action.isPending}>
                            <Send className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Submit
                          </Button>
                        ) : null}
                        {isDraft ? (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(e)}>
                            <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Edit
                          </Button>
                        ) : null}
                        <Button size="sm" variant="ghost" onClick={() => openDuplicate(e)}>
                          <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Duplicate
                        </Button>
                        {isDraft ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (!window.confirm(`Delete ${e.expenseNo}? This cannot be undone.`)) return;
                              remove.mutate(e._id, {
                                onSuccess: () => toast.success(`${e.expenseNo} deleted`),
                                onError: (err: unknown) => toast.error(getApiErrorMessage(err) || 'Delete failed'),
                              });
                            }}
                            disabled={remove.isPending}
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Delete
                          </Button>
                        ) : (
                          e.paymentStatus === 'unpaid' && !['cancelled', 'refunded'].includes(e.status) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const reason = window.prompt('Reason for cancelling this expense?');
                                if (reason && reason.trim()) run(e, 'cancel', { reason: reason.trim() });
                              }}
                              disabled={action.isPending}
                            >
                              <Ban className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Cancel
                            </Button>
                          ) : null
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* The one and only expense form, shared with the Expense Register. */}
      {formOpen ? (
        <ExpenseFormModal
          key={`${formMode}-${target?._id ?? 'new'}`}
          open
          mode={formMode}
          onClose={() => setFormOpen(false)}
          expense={target}
        />
      ) : null}
    </div>
  );
}

export default ExpenseWorkspaceTab;
